<template>
  <div class="mystocks">
    <mainmenu selected="mystocks"></mainmenu>

    <div class="container-fluid mystocks-shell">
      <section class="stock-command-card">
        <div class="stock-page-header">
          <div>
            <h1>{{ $t("mystocks.page_title") }}</h1>
            <div class="stock-context-line">
              <span class="factory-pill">{{ activeFactoryLabel }}</span>
              <span>{{ $t("mystocks.laundry_offer_scope") }}</span>
            </div>
          </div>

          <div class="stock-actions">
            <el-select
              v-if="factories.length > 1"
              class="factory-select"
              size="small"
              v-model="activeFactoryId"
              @change="change_active_factory">
              <el-option
                v-for="factory in factories"
                :key="factory.Id"
                :label="formatFactory(factory)"
                :value="String(factory.Id)">
              </el-option>
            </el-select>

            <el-button type="primary" icon="el-icon-plus" @click="open_create_dialog">
              {{ $t("mystocks.create_offer") }}
            </el-button>
          </div>
        </div>

        <div class="stock-summary-grid">
          <div class="summary-tile">
            <span>{{ $t("mystocks.active_offers") }}</span>
            <strong>{{ formatNumber(summary.OfferCount) }}</strong>
          </div>
          <div class="summary-tile summary-tile-catalog">
            <span>{{ $t("mystocks.usable_products") }}</span>
            <strong>{{ formatNumber(summary.UsableProductCount) }}</strong>
            <em>{{ formatNumber(summary.MissingPriceProductCount) }} {{ $t("mystocks.need_price") }}</em>
          </div>
          <div class="summary-tile">
            <span>{{ $t("mystocks.available_quantity") }}</span>
            <strong>{{ formatNumber(summary.AvailableQuantity) }}</strong>
          </div>
          <div class="summary-tile">
            <span>{{ $t("mystocks.ordered_quantity") }}</span>
            <strong>{{ formatNumber(summary.OrderedQuantity) }}</strong>
          </div>
          <div class="summary-tile">
            <span>{{ $t("mystocks.estimated_value") }}</span>
            <strong>{{ formatMoney(summary.EstimatedValue) }}</strong>
          </div>
        </div>

        <div class="mystocks-filter-panel">
          <span class="filters-label">{{ $t("mystocks.filters") }}</span>
          <div class="filters-grid">
            <label class="filter-field filter-article">
              <span>{{ $t("dashboard.article_code") }}</span>
              <el-input
                size="small"
                v-model="Filters.Name"
                :placeholder="$t('mystocks.search_article')"
                clearable
                @keyup.enter.native="refresh_info">
              </el-input>
            </label>

            <label class="filter-field filter-quantity">
              <span>{{ $t("dashboard.quantity_range") }}</span>
              <div class="quantity-range">
                <el-input
                  size="small"
                  type="number"
                  v-model="Filters.MinQuantity"
                  :placeholder="$t('dashboard.min_quantity')">
                </el-input>
                <span class="range-divider">-</span>
                <el-input
                  size="small"
                  type="number"
                  v-model="Filters.MaxQuantity"
                  :placeholder="$t('dashboard.max_quantity')">
                </el-input>
              </div>
            </label>

            <div class="filter-actions">
              <el-button size="small" type="primary" icon="el-icon-search" @click="refresh_info">{{ $t("mystocks.filter") }}</el-button>
              <el-button size="small" icon="el-icon-refresh-left" @click="reset_info">{{ $t("mystocks.reset") }}</el-button>
            </div>
          </div>
        </div>
      </section>

      <section class="mystocks-table-card">
        <div class="table-card-header">
          <div>
            <div class="table-title">{{ $t("mystocks.stock_list") }}</div>
            <div class="table-subtitle">{{ $t("mystocks.sorted_by_activity") }}</div>
          </div>
          <span class="table-total">{{ $t("mystocks.total") }} {{ formatNumber(products.length) }}</span>
        </div>

        <el-table
          class="mystocks-table"
          :data="products"
          :height="tableHeight"
          stripe
          style="width: 100%"
          v-loading="tableLoadingVisible"
          :empty-text="$t('mystocks.empty_results')">
          <el-table-column :label="$t('mystocks.product')" min-width="390">
            <template slot-scope="scope">
              <div class="article-main">
                <strong>{{ scope.row.ArticleNumber }}</strong> - {{ scope.row.Name }}
              </div>
              <div class="article-meta">
                <span>{{ scope.row.ProductCategory }}</span>
                <span v-if="scope.row.Description">{{ scope.row.Description }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('mystocks.available_quantity')" width="175" align="right">
            <template slot-scope="scope">
              <div class="quantity-editor" v-loading="scope.row.used_loading">
                <button
                  v-if="!scope.row.show_edit_used"
                  type="button"
                  class="quantity-button"
                  @click="start_edit_used_quantity(scope.row)">
                  {{ formatNumber(scope.row.UsedQuantity) }}
                </button>

                <div class="quantity-edit-form" v-if="scope.row.show_edit_used">
                  <el-input-number
                    size="mini"
                    class="quantity-input"
                    v-model.number="scope.row.UsedQuantity"
                    :min="0">
                  </el-input-number>
                  <div class="quantity-actions">
                    <el-button size="mini" icon="el-icon-close" @click="cancel_used_quantity(scope.row)">{{ $t("cancel") }}</el-button>
                    <el-button size="mini" type="primary" icon="el-icon-check" @click="save_used_quantity(scope.row)">{{ $t("save") }}</el-button>
                  </div>
                </div>
              </div>
            </template>
          </el-table-column>

          <el-table-column :label="$t('mystocks.ordered_quantity')" width="145" align="right">
            <template slot-scope="scope">
              <span class="number-cell">{{ formatNumber(scope.row.OrderedQuantity) }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('mystocks.price')" width="120" align="right">
            <template slot-scope="scope">
              <span class="number-cell">{{ formatMoney(scope.row.Price) }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('mystocks.last_modified_date')" width="155">
            <template slot-scope="scope">
              <span class="date-chip" v-if="formatDate(scope.row.LastModifiedDate) !== '-'">
                {{ formatDate(scope.row.LastModifiedDate) }}
              </span>
              <span class="empty-date" v-else>-</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('mystocks.last_updated_by')" width="170">
            <template slot-scope="scope">
              <span>{{ scope.row.LastUpdatedByUserName || scope.row.SellerName || "-" }}</span>
            </template>
          </el-table-column>

          <el-table-column :label="$t('mystocks.operations')" width="145" align="right">
            <template slot-scope="scope">
              <div class="row-actions">
                <el-button size="mini" type="text" @click="start_edit_used_quantity(scope.row)">{{ $t("edit") }}</el-button>
                <el-button size="mini" type="text" class="danger-link" @click="close_offer(scope.row)">{{ $t("mystocks.close_offer") }}</el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </section>
    </div>

    <el-dialog
      :title="$t('mystocks.create_offer')"
      :visible.sync="createDialogVisible"
      width="640px"
      custom-class="create-offer-dialog">
      <div class="offer-dialog-body">
        <div class="dialog-context">
          <span>{{ $t("mystocks.laundry") }}</span>
          <strong>{{ activeFactoryLabel }}</strong>
        </div>

        <label class="dialog-field">
          <span>{{ $t("mystocks.product") }}</span>
          <div class="product-picker-status">
            <div>
              <strong>{{ formatNumber(productSearchStats.UsableProductCount) }}</strong>
              <span>{{ $t("mystocks.usable_products") }}</span>
            </div>
            <div>
              <strong>{{ formatNumber(productSearchStats.MissingPriceProductCount) }}</strong>
              <span>{{ $t("mystocks.need_price") }}</span>
            </div>
            <em v-if="productSearchQuery">{{ $t("mystocks.product_search_results") }}</em>
            <em v-else>{{ $t("mystocks.product_search_starter") }}</em>
          </div>
          <el-select
            class="product-search"
            v-model="offerForm.ProductId"
            filterable
            remote
            reserve-keyword
            default-first-option
            clearable
            popper-class="product-search-popper"
            :placeholder="$t('mystocks.search_product_placeholder')"
            :remote-method="search_products"
            :loading="productSearchLoading"
            :no-data-text="$t('mystocks.no_usable_products_found')"
            @clear="search_products('')"
            @visible-change="on_product_search_visible"
            @change="on_product_selected">
            <el-option
              v-for="product in productOptions"
              :key="product.Id"
              :label="formatProductOption(product)"
              :value="product.Id">
              <div class="product-option">
                <div class="product-option-code">{{ product.ArticleNumber }}</div>
                <div class="product-option-copy">
                  <strong>{{ product.Name }}</strong>
                  <span>{{ product.ProductCategory }}</span>
                </div>
                <div class="product-option-meta">
                  <b>{{ formatMoney(product.Price, product.Currency) }}</b>
                  <em v-if="Number(product.ExistingQuantity) > 0">
                    {{ $t("mystocks.existing_offer") }} {{ formatNumber(product.ExistingQuantity) }}
                  </em>
                  <em v-else-if="Number(product.ActiveOfferCount) > 0">
                    {{ formatNumber(product.ActiveOfferCount) }} {{ $t("mystocks.marketplace_offers") }}
                  </em>
                </div>
              </div>
            </el-option>
          </el-select>
        </label>

        <div class="selected-product-card" v-if="selectedProduct">
          <div>
            <strong>{{ selectedProduct.ArticleNumber }} - {{ selectedProduct.Name }}</strong>
            <span>{{ selectedProduct.ProductCategory }}</span>
          </div>
          <el-tag type="success" size="mini">
            {{ formatMoney(selectedProduct.Price, selectedProduct.Currency) }}
          </el-tag>
          <el-tag v-if="Number(selectedProduct.ExistingQuantity) > 0" type="warning" size="mini">
            {{ $t("mystocks.existing_offer") }}: {{ formatNumber(selectedProduct.ExistingQuantity) }}
          </el-tag>
        </div>

        <label class="dialog-field">
          <span>{{ $t("mystocks.available_quantity") }}</span>
          <el-input-number
            class="dialog-quantity"
            v-model.number="offerForm.Quantity"
            :min="1"
            :step="1">
          </el-input-number>
        </label>
      </div>

      <span slot="footer" class="dialog-footer">
        <el-button @click="createDialogVisible = false">{{ $t("cancel") }}</el-button>
        <el-button type="primary" :loading="createOfferLoading" @click="create_offer">
          {{ $t("mystocks.publish_offer") }}
        </el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import MainMenu from "./../widgets/MainMenu";
import settings from "./../backend/LocalSettings";
import { EventBus } from "./../backend/EventBus.js";
import { countryLabel } from "./../utils/countries";
import { intlLocaleForLanguage } from "./../utils/languages";

function buildDefaultFilters() {
  return { Name: "", MinQuantity: "", MaxQuantity: "" };
}

function buildDefaultSummary() {
  return {
    OfferCount: 0,
    AvailableQuantity: 0,
    OrderedQuantity: 0,
    EstimatedValue: 0,
    UsableProductCount: 0,
    MissingPriceProductCount: 0
  };
}

function buildDefaultProductSearchStats() {
  return { UsableProductCount: 0, MissingPriceProductCount: 0, ResultCount: 0 };
}

export default {
  name: "my-stocks",
  components: {
    "mainmenu": MainMenu
  },
  data () {
    return {
      tableLoadingVisible: false,
      tableHeight: 640,
      products: [],
      factories: [],
      activeFactoryId: "",
      activeFactory: null,
      summary: buildDefaultSummary(),
      Filters: buildDefaultFilters(),
      createDialogVisible: false,
      createOfferLoading: false,
      productSearchLoading: false,
      productOptions: [],
      productSearchQuery: "",
      productSearchStats: buildDefaultProductSearchStats(),
      selectedProduct: null,
      offerForm: {
        ProductId: "",
        Quantity: 1
      }
    };
  },
  computed: {
    activeFactoryLabel: function() {
      return this.activeFactory ? this.formatFactory(this.activeFactory) : this.$t("mystocks.no_laundry_selected");
    }
  },
  methods: {
    updateTableHeight: function() {
      var viewportHeight = window.innerHeight || document.documentElement.clientHeight || 900;
      this.tableHeight = Math.max(560, Math.floor(viewportHeight - 402));
    },
    formatFactory: function(factory) {
      if(!factory) return "";
      if(factory.Country) return factory.ShortCode + " - " + countryLabel(this, factory.Country);
      return factory.ShortCode;
    },
    setActiveFactoryFromList: function(id) {
      var activeId = parseInt(id);
      this.activeFactory = this.factories.find(function(factory) {
        return parseInt(factory.Id) === activeId;
      }) || this.activeFactory;
    },
    load_factory_context: function() {
      this.$http
        .post("mystocks/factory_context", {})
        .then(function(response) {
          if(settings.verify_response(response, this.$rights)) {
            this.factories = response.body.Factories || [];
            var storedFactoryId = settings.get_active_factory_id();
            var preferredFactoryId = storedFactoryId || response.body.ActiveFactoryId || response.body.PrimaryFactoryId || "";
            var allowed = this.factories.some(function(factory) {
              return parseInt(factory.Id) === parseInt(preferredFactoryId);
            });

            if(!allowed && this.factories.length > 0) preferredFactoryId = this.factories[0].Id;

            this.activeFactoryId = preferredFactoryId ? String(preferredFactoryId) : "";
            if(this.activeFactoryId) settings.set_active_factory_id(this.activeFactoryId);
            this.setActiveFactoryFromList(this.activeFactoryId);
            this.refresh_info();
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          this.refresh_info();
        });
    },
    change_active_factory: function(factoryId) {
      this.activeFactoryId = String(factoryId);
      settings.set_active_factory_id(factoryId);
      EventBus.$emit("on_factory_context_change", factoryId);
    },
    save_used_quantity: function(p) {
      this.save_quantity(p);
    },
    reset_info: function() {
      this.Filters = buildDefaultFilters();
      this.refresh_info();
    },
    save_quantity: function(p) {
      p.used_loading = true;

      this.$http
        .post("mystocks/save_quantity", { id_product: p.Id, quantity: p.UsedQuantity })
        .then(function(response) {
          if(settings.verify_response(response, this.$rights)) {
            p.used_loading = false;
            if(response.body.Error) {
              this.$message.error(response.body.ErrorMessage || this.$t("mystocks.save_failed"));
              return;
            }
            p.show_edit_used = false;
            this.$message.success(this.$t("mystocks.offer_saved"));
            this.refresh_info();
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          p.used_loading = false;
          this.$message.error(this.$t("mystocks.save_failed"));
        });
    },
    start_edit_used_quantity: function(p) {
      p.show_edit_used = true;
      p.saved_used_quantity = p.UsedQuantity;
    },
    cancel_used_quantity: function(p) {
      p.show_edit_used = false;
      p.UsedQuantity = p.saved_used_quantity;
    },
    close_offer: function(p) {
      this.$confirm(this.$t("mystocks.confirm_close_offer"), this.$t("warning_title"), {
        confirmButtonText: this.$t("mystocks.close_offer"),
        cancelButtonText: this.$t("cancel"),
        type: "warning"
      }).then(function() {
        p.UsedQuantity = 0;
        this.save_quantity(p);
      }.bind(this)).catch(function() {});
    },
    refresh_info: function() {
      this.tableLoadingVisible = true;
      this.$http
        .post("mystocks/refresh_info", { Filters: this.Filters })
        .then(function(response) {
          this.tableLoadingVisible = false;
          if(settings.verify_response(response, this.$rights)) {
            this.products = response.body.products || [];
            this.summary = response.body.Summary || buildDefaultSummary();
            if(response.body.ActiveFactory) {
              this.activeFactory = response.body.ActiveFactory;
              this.activeFactoryId = String(response.body.ActiveFactory.Id);
            }
            this.updateTableHeight();
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          this.tableLoadingVisible = false;
          this.updateTableHeight();
          this.$message.error(this.$t("mystocks.load_failed"));
        });
    },
    on_factory_context_change: function(factoryId) {
      if(factoryId) {
        this.activeFactoryId = String(factoryId);
        settings.set_active_factory_id(factoryId);
        this.setActiveFactoryFromList(factoryId);
      }
      this.refresh_info();
    },
    open_create_dialog: function() {
      this.offerForm = { ProductId: "", Quantity: 1 };
      this.productOptions = [];
      this.productSearchQuery = "";
      this.productSearchStats = {
        UsableProductCount: this.summary.UsableProductCount || 0,
        MissingPriceProductCount: this.summary.MissingPriceProductCount || 0,
        ResultCount: 0
      };
      this.selectedProduct = null;
      this.createDialogVisible = true;
      this.$nextTick(function() {
        this.search_products("");
      });
    },
    search_products: function(query) {
      query = query || "";
      this.productSearchQuery = query;
      this.productSearchLoading = true;
      this.$http
        .post("mystocks/search_products", { query: query })
        .then(function(response) {
          this.productSearchLoading = false;
          if(settings.verify_response(response, this.$rights)) {
            this.productOptions = response.body.Results || [];
            this.productSearchStats = Object.assign(
              buildDefaultProductSearchStats(),
              response.body.CatalogSummary || {},
              { ResultCount: response.body.ResultCount || (response.body.Results || []).length }
            );
          }
        })
        .catch(function() {
          this.productSearchLoading = false;
          this.$message.error(this.$t("mystocks.product_search_failed"));
        });
    },
    on_product_selected: function(productId) {
      this.selectedProduct = this.productOptions.find(function(product) {
        return parseInt(product.Id) === parseInt(productId);
      }) || null;
      if(this.selectedProduct && Number(this.selectedProduct.ExistingQuantity) > 0) {
        this.offerForm.Quantity = Number(this.selectedProduct.ExistingQuantity);
      }
    },
    on_product_search_visible: function(visible) {
      if(visible && this.productOptions.length === 0 && !this.productSearchLoading) {
        this.search_products(this.productSearchQuery || "");
      }
    },
    create_offer: function() {
      if(!this.offerForm.ProductId) {
        this.$message.warning(this.$t("mystocks.select_product_first"));
        return;
      }
      if(!this.offerForm.Quantity || Number(this.offerForm.Quantity) <= 0) {
        this.$message.warning(this.$t("mystocks.enter_quantity_first"));
        return;
      }

      this.createOfferLoading = true;
      this.$http
        .post("mystocks/save_quantity", {
          id_product: this.offerForm.ProductId,
          quantity: this.offerForm.Quantity
        })
        .then(function(response) {
          this.createOfferLoading = false;
          if(settings.verify_response(response, this.$rights)) {
            if(response.body.Error) {
              this.$message.error(response.body.ErrorMessage || this.$t("mystocks.save_failed"));
              return;
            }
            this.createDialogVisible = false;
            this.$message.success(this.$t("mystocks.offer_saved"));
            this.refresh_info();
          } else {
            this.$router.push("/");
          }
        })
        .catch(function() {
          this.createOfferLoading = false;
          this.$message.error(this.$t("mystocks.save_failed"));
        });
    },
    formatProductOption: function(product) {
      if(!product) return "";
      return product.ArticleNumber + " - " + product.Name;
    },
    formatDate: function(value) {
      if(!value || value === "-1" || value === "0000-00-00 00:00:00") return "-";
      var parts = String(value).split(/[- :]/);
      if(parts.length < 5) return value;
      var dateValue = new Date(Date.UTC(
        Number(parts[0]),
        Number(parts[1]) - 1,
        Number(parts[2]),
        Number(parts[3] || 0),
        Number(parts[4] || 0),
        Number(parts[5] || 0)
      ));
      if(isNaN(dateValue.getTime())) return value;
      var pad = function(part) { return String(part).padStart(2, "0"); };
      return pad(dateValue.getDate()) + "." + pad(dateValue.getMonth() + 1) + "." + dateValue.getFullYear();
    },
    formatNumber: function(value) {
      if(value === null || value === undefined || value === "") return "0";
      return new Intl.NumberFormat(intlLocaleForLanguage(this.$i18n && this.$i18n.locale)).format(Number(value));
    },
    formatMoney: function(value, currency) {
      var locale = intlLocaleForLanguage(this.$i18n && this.$i18n.locale);
      var safeCurrency = currency || "EUR";
      try {
        return new Intl.NumberFormat(locale, {
          style: "currency",
          currency: safeCurrency,
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(Number(value || 0));
      } catch(e) {
        return new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        }).format(Number(value || 0)) + " " + safeCurrency;
      }
    }
  },
  mounted: function() {
    this.updateTableHeight();
    window.addEventListener("resize", this.updateTableHeight);
    EventBus.$on("on_factory_context_change", this.on_factory_context_change);
    this.load_factory_context();
  },
  beforeDestroy: function() {
    window.removeEventListener("resize", this.updateTableHeight);
    EventBus.$off("on_factory_context_change", this.on_factory_context_change);
  }
};
</script>

<style lang="less" scoped>
.mystocks-shell {
  min-height: calc(100vh - 54px);
  padding: 12px 10px 10px;
}

.stock-command-card,
.mystocks-table-card {
  border: 1px solid rgba(26, 88, 111, 0.12);
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 12px 26px rgba(25, 80, 99, 0.13);
}

.stock-command-card {
  margin-bottom: 10px;
  padding: 14px 18px 12px;
}

.stock-page-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8eef2;

  h1 {
    margin: 0;
    color: #152b38;
    font-size: 29px;
    font-weight: 700;
    letter-spacing: 0;
    line-height: 33px;
  }
}

.stock-context-line {
  display: flex;
  align-items: center;
  gap: 9px;
  margin-top: 4px;
  color: rgba(21, 43, 56, 0.72);
  font-size: 13px;
  font-weight: 600;
}

.factory-pill {
  display: inline-flex;
  align-items: center;
  min-height: 25px;
  padding: 0 10px;
  border: 1px solid #b8dfe8;
  border-radius: 4px;
  background: #effbfc;
  color: #146c82;
  font-weight: 800;
}

.stock-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
}

.factory-select {
  width: 240px;
}

.stock-summary-grid {
  display: grid;
  grid-template-columns: repeat(5, minmax(140px, 1fr));
  gap: 10px;
  margin-bottom: 12px;
}

.summary-tile {
  min-height: 64px;
  padding: 12px 14px;
  border: 1px solid #dce9ee;
  border-radius: 6px;
  background: #f7fbfc;

  span {
    display: block;
    color: #607581;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  strong {
    display: block;
    margin-top: 4px;
    color: #173242;
    font-size: 23px;
    font-weight: 800;
    line-height: 26px;
  }

  em {
    display: block;
    margin-top: 2px;
    color: #7c8b94;
    font-size: 11px;
    font-style: normal;
    font-weight: 700;
    line-height: 15px;
  }
}

.summary-tile-catalog {
  border-color: #bfe1eb;
  background: linear-gradient(180deg, #f5fcfe 0%, #eef8fb 100%);
}

.mystocks-filter-panel {
  display: grid;
  grid-template-columns: 54px 1fr;
  align-items: center;
  gap: 14px;
}

.filters-label {
  color: #2e4a5a;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
  text-transform: uppercase;
}

.filters-grid {
  display: grid;
  grid-template-columns: minmax(220px, 1fr) minmax(260px, 1.05fr) auto;
  align-items: end;
  gap: 10px;
}

.filter-field,
.dialog-field {
  display: block;
  margin: 0;
  min-width: 0;

  > span {
    display: block;
    margin-bottom: 5px;
    color: #556b78;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0;
    text-transform: uppercase;
  }

  /deep/ .el-input__inner {
    border-color: #d7e0e7;
    color: #324a59;
    font-weight: 600;
  }
}

.quantity-range {
  display: grid;
  grid-template-columns: minmax(90px, 1fr) auto minmax(110px, 1fr);
  align-items: center;
  gap: 8px;

  /deep/ .el-input__inner {
    text-align: right;
  }
}

.range-divider {
  color: #74838c;
  font-weight: 700;
}

.filter-actions,
.quantity-actions,
.row-actions {
  display: flex;
  align-items: center;
  gap: 8px;

  .el-button + .el-button {
    margin-left: 0;
  }
}

.filter-actions {
  padding-bottom: 1px;
  white-space: nowrap;
}

.mystocks-table-card {
  overflow: hidden;
  background: #fff;
}

.table-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 50px;
  padding: 10px 18px;
  border-bottom: 1px solid #e6edf2;
  background: #fbfdfe;
}

.table-title {
  color: #223947;
  font-size: 15px;
  font-weight: 800;
  line-height: 20px;
}

.table-subtitle {
  color: #6d7f89;
  font-size: 12px;
  font-weight: 600;
}

.table-total {
  display: inline-flex;
  align-items: center;
  min-height: 26px;
  padding: 0 10px;
  border-radius: 4px;
  background: #eef6f9;
  color: #2b6275;
  font-size: 12px;
  font-weight: 800;
}

.mystocks-table {
  background: #fff;
}

.mystocks-table /deep/ th {
  padding: 8px 0;
  background: #f4f8fa;
  color: #526875;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0;
}

.mystocks-table /deep/ td {
  padding: 7px 0;
  border-bottom-color: #e7edf2;
  color: #405461;
}

.mystocks-table /deep/ .cell {
  line-height: 19px;
}

.mystocks-table /deep/ .el-table__row:hover > td {
  background: #f4fbfd;
}

.article-main {
  color: #314754;
  line-height: 19px;
}

.article-meta {
  display: flex;
  gap: 10px;
  margin-top: 2px;
  color: #657987;
  font-size: 12px;
  font-style: italic;
  line-height: 16px;
}

.date-chip,
.number-cell {
  color: #314d5d;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
}

.empty-date {
  color: #a0adb5;
}

.quantity-button {
  min-width: 86px;
  padding: 3px 10px;
  border: 1px solid #cfe4ee;
  border-radius: 4px;
  background: #f4fbfd;
  color: #167b95;
  cursor: pointer;
  font: inherit;
  font-variant-numeric: tabular-nums;
  font-weight: 800;
  text-align: right;
  transition: border-color 0.16s ease, background 0.16s ease, color 0.16s ease;

  &:hover,
  &:focus {
    border-color: #2d9db4;
    background: #ebf8fb;
    color: #0d6175;
    outline: none;
  }
}

.quantity-edit-form {
  display: grid;
  gap: 8px;
  justify-items: end;
}

.quantity-input {
  width: 150px;
}

.danger-link {
  color: #d9534f;
}

.offer-dialog-body {
  display: grid;
  gap: 16px;
}

.dialog-context {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  border: 1px solid #dce9ee;
  border-radius: 6px;
  background: #f8fbfc;

  span {
    color: #607581;
    font-size: 11px;
    font-weight: 800;
    text-transform: uppercase;
  }

  strong {
    color: #173242;
  }
}

.product-search {
  width: 100%;
}

.product-picker-status {
  display: grid;
  grid-template-columns: auto auto 1fr;
  align-items: center;
  gap: 8px;
  margin: 0 0 8px;

  div {
    display: inline-flex;
    align-items: center;
    min-height: 28px;
    padding: 0 9px;
    border: 1px solid #cfe4ee;
    border-radius: 4px;
    background: #f5fbfd;
    color: #48616f;
    font-size: 11px;
    font-weight: 800;
    white-space: nowrap;
  }

  strong {
    margin-right: 5px;
    color: #143444;
    font-size: 14px;
  }

  em {
    justify-self: end;
    color: #6f808a;
    font-size: 12px;
    font-style: normal;
    font-weight: 600;
  }
}

.product-option {
  display: grid;
  grid-template-columns: 96px minmax(0, 1fr) auto;
  align-items: center;
  column-gap: 12px;
  min-height: 52px;
  padding: 6px 0;
  line-height: 18px;
}

.product-option-code {
  color: #153242;
  font-weight: 800;
  font-variant-numeric: tabular-nums;
}

.product-option-copy {
  min-width: 0;

  strong,
  span {
    display: block;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  strong {
    color: #203947;
  }

  span {
    color: #324a59;
    font-size: 12px;
  }
}

.product-option-meta {
  display: grid;
  justify-items: end;
  min-width: 118px;

  b {
    color: #0f6f86;
    font-size: 13px;
    font-weight: 800;
  }

  em {
    color: #7a8b95;
    font-size: 12px;
    font-style: normal;
    white-space: nowrap;
  }
}

.selected-product-card {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 12px;
  border: 1px solid #dce9ee;
  border-radius: 6px;
  background: #fbfdfe;

  div {
    display: grid;
    gap: 3px;
  }

  span {
    color: #667986;
    font-size: 12px;
  }

  .el-tag {
    flex: 0 0 auto;
  }
}

.dialog-quantity {
  width: 180px;
}

@media (max-width: 1180px) {
  .stock-page-header,
  .stock-actions {
    align-items: stretch;
    flex-direction: column;
  }

  .factory-select {
    width: 100%;
  }

  .stock-summary-grid,
  .mystocks-filter-panel,
  .filters-grid,
  .product-picker-status {
    grid-template-columns: 1fr;
  }

  .product-picker-status em {
    justify-self: start;
  }
}

@media (max-width: 760px) {
  .stock-page-header h1 {
    font-size: 28px;
  }

  .table-card-header,
  .quantity-actions,
  .row-actions,
  .selected-product-card {
    align-items: flex-start;
    flex-direction: column;
  }
}
</style>

<style lang="less">
.product-search-popper {
  .el-select-dropdown__item {
    height: auto;
    padding: 0 14px;
    line-height: normal;
  }

  .el-select-dropdown__item.selected,
  .el-select-dropdown__item.hover {
    background: #eef8fb;
  }
}
</style>
